import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAppSelector, useAppDispatch } from "../../../Redux/redux-store";
import {
  GetRealCurrentFrame,
  GetTotalFrameCount,
  GetFrameRate,
  GetLanguages,
  GetPlayingState,
  GetVolume,
  GoToFrame,
  PlayPause,
  SetActiveLanguages,
  SetVolume,
  GetFullScreen,
  SetPlayerFullScreen,
  GetIsFileTCDisplayed,
  GetPlaybackSpeed,
  SetPlaybackSpeed,
  SetFileTCDisplayed,
  SetPlayerPip,
  GoToLastFrame,
  GetTimecodeIn,
  GetPlayerSetupData,
  GoToFrameDelta,
  SetPlayerBackground,
  SetSeek,
  GetTimecode,
  GetFileTimecode,
} from "../../../Redux/Reducers/redux-slice-react-player";
import {
  ArrowLeft,
  ArrowRight,
  CameraAltOutlined,
  CheckOutlined,
  ExpandMoreOutlined,
  Fullscreen,
  FullscreenExit,
  ImageOutlined,
  KeyboardReturn,
  Pause,
  PictureInPictureAltOutlined,
  PlayArrowOutlined,
  SettingsOutlined,
  SubtitlesOutlined,
  VolumeDownOutlined,
  VolumeOffOutlined,
  VolumeUpOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  ClickAwayListener,
  Collapse,
  IconButton,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Slider,
  Stack,
  styled,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import InputTimecode from "../../../Utils/Timecode/Timecode";
import { UserHasAcl, UserHasLicence } from "../../../Utils/helpers";
import { Licences } from "../../../Core/Api/Types/licences";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { cacheService, mediaService, thumbnailService } from "../../../Core/Api/services";

import { FileTimecodeToTimecode, FrameToMs, SecToFrame, TimecodeToFrame } from "../../../Utils/Timecode/ReactPlayer";
import useEventListener from "../../../Utils/Hooks/useEventListener";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { PlatformMode } from "../../../Core/Api/Types/platform-mode";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { PreviewContext } from "../../../Pages/admin/Inventory/InventoryAssetSingle/AssetSinglePreview/asset-single-preview";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { Store } from "@reduxjs/toolkit";
import { useStore } from "react-redux";
import { useTranslation } from "react-i18next";
import { GetObject } from "../../../Redux/Reducers/redux-slice-asset";
import { IsDriveObject } from "../../../Core/Utils/asset-object-utils";
import {
  SetActiveTimeline,
  SetCreatingOrEditingTimelineEvent,
  TimelineType,
} from "../../../Redux/Reducers/redux-slice-timeline";
import useIsMobile from "../../../Utils/Hooks/useIsMobile";

export const PlayerControls = (props: { assetType: ObjectType; context: PreviewContext }) => {
  const { assetType, context } = props;
  const platformMode = cacheService.GetPlatformMode();
  const currentObject = useAppSelector(GetObject);
  const isDrive = useMemo(
    () => platformMode === PlatformMode.Browser || (currentObject && IsDriveObject(currentObject)),
    [platformMode, currentObject]
  );
  const isAdam = useMemo(() => platformMode === PlatformMode.Adam, [platformMode]);
  const isMobile = useIsMobile();
  const isPlayerFullScreen = useAppSelector(GetFullScreen);

  return (
    <Stack
      direction="row"
      justifyContent={"space-between"}
      sx={(theme) => ({
        alignItems: "center",
        height: "3rem",
        px: 0.5,
        ...(!isPlayerFullScreen && {
          background: theme.palette.background.paper,
        }),
      })}
    >
      <Stack direction="row" flexBasis={"33%"} alignItems={"center"}>
        <PlayButton />
        <VolumeSlider />
        <TotalTimer />
      </Stack>
      {![
        PreviewContext.DriveFolderPreview,
        PreviewContext.DriveCollectionPreview,
        PreviewContext.DistribInventoryPreview,
        PreviewContext.DistribCollectionPreview,
      ].includes(context) ? (
        <>
          {!isMobile && <TimecodeControls />}
          <Stack direction="row" justifyContent={"flex-end"} flexBasis={"33%"}>
            <LanguagesButton />
            {assetType === ObjectType.Video && <ScreenshotButton />}
            {assetType === ObjectType.Video && (isDrive || isAdam) && <ThumbnailButton />}
            {!isMobile && <SettingsButton />}
            {assetType === ObjectType.Video && !isMobile && <PipButton />}
            <FullScreenButton />
          </Stack>
        </>
      ) : (
        <>
          {!isMobile && <TimecodeControls hidden />}
          <Stack direction="row" justifyContent={"flex-end"} flexBasis={"33%"}>
            {!isMobile && <SettingsButton />}
            <FullScreenButton />
          </Stack>
        </>
      )}
    </Stack>
  );
};

export const TotalTimer = () => {
  const currFrame = useAppSelector(GetRealCurrentFrame);
  const totalFrameCount = useAppSelector(GetTotalFrameCount);
  const frameRate = useAppSelector(GetFrameRate);

  const currTime = useMemo(() => currFrame / frameRate, [currFrame, frameRate]);
  const totalDuration = useMemo(() => totalFrameCount / frameRate, [totalFrameCount, frameRate]);

  const secondsToHms = (s: number) => {
    const hours = Math.floor((s - (s % 3600)) / 3600);
    const minutes = Math.floor(((s - (s % 60)) / 60) % 60);
    const seconds = Math.floor(s % 60);
    return `${hours > 0 ? String(hours).padStart(2, "0") + ":" : ""}${String(minutes).padStart(2, "0")}:${String(
      seconds
    ).padStart(2, "0")}`;
  };

  return (
    <Stack direction="row" justifyContent={"center"} ml={1}>
      <Typography variant="body1" color="text.secondary">
        {secondsToHms(currTime)}&nbsp;/&nbsp;
      </Typography>
      <Typography variant="body1" color="text.secondary">
        {secondsToHms(totalDuration)}
      </Typography>
    </Stack>
  );
};

export const PlayButton = () => {
  const { t } = useTranslation();
  const isPlaying = useAppSelector(GetPlayingState);
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLButtonElement>(null);

  const handleKeyPress = (e: KeyboardEvent) => {
    const activeElement = document.activeElement;
    if (
      activeElement &&
      ((activeElement.tagName.toLowerCase() === "input" && activeElement.ariaLabel !== "time-indicator") ||
        activeElement.tagName.toLowerCase() === "textarea")
    ) {
      return;
    }

    switch (e.key) {
      case " ":
      case "Space":
        e.preventDefault();
        e.stopPropagation();
        dispatch(PlayPause());
        break;
    }
  };

  useEventListener("keydown", handleKeyPress);

  return (
    <Tooltip
      slotProps={{
        popper: {
          container: containerRef.current,
        },
      }}
      followCursor
      placement="top"
      title={<KeyboardKey>{t("tooltip:title.space")}</KeyboardKey>}
    >
      <IconButton ref={containerRef} onClick={(e) => dispatch(PlayPause())}>
        {isPlaying ? <Pause /> : <PlayArrowOutlined />}
      </IconButton>
    </Tooltip>
  );
};

export const VolumeSlider = () => {
  const [volumeLocal, setVolumeLocal] = useState<number>(30);
  const [hover, setHover] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const volumeRedux = useAppSelector(GetVolume);

  useEffect(() => {
    setVolumeLocal(volumeRedux);
  }, [volumeRedux]);

  const handleVolumeChange = useCallback((event: Event, value: number | number[]) => {
    setVolumeLocal(value as number);
    dispatch(SetVolume(value as number));
  }, []);

  const handleToggleMute = useCallback(() => {
    if (volumeLocal > 0) {
      setVolumeLocal(0);
      dispatch(SetVolume(0));
    } else {
      setVolumeLocal(20);
      dispatch(SetVolume(20));
    }
  }, [volumeLocal]);

  const onMouseEnter = useCallback(() => {
    setHover(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    setHover(false);
  }, []);

  return (
    <Collapse in={hover} orientation="horizontal" collapsedSize={40}>
      <Stack
        spacing={1}
        direction="row"
        alignItems="center"
        sx={{}}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <IconButton color="ghost" onClick={handleToggleMute}>
          {volumeLocal === 0 ? <VolumeOffOutlined /> : volumeLocal < 50 ? <VolumeDownOutlined /> : <VolumeUpOutlined />}
        </IconButton>

        <Slider
          size="small"
          min={0}
          max={100}
          sx={{ width: 60 }}
          aria-label="Volume"
          value={volumeLocal}
          onChange={handleVolumeChange}
        />
      </Stack>
    </Collapse>
  );
};

export const FullScreenButton = () => {
  const fullScreen = useAppSelector(GetFullScreen);
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLButtonElement>(null);
  const [hover, setHover] = useState<boolean>(false);

  const handleFullScreen = () => {
    dispatch(SetPlayerFullScreen(!fullScreen));
    setHover(false);
  };

  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName.toLowerCase() === "input" || activeElement.tagName.toLowerCase() === "textarea")
      ) {
        return;
      }

      switch (e.key) {
        case "Escape":
          if (fullScreen) {
            dispatch(SetPlayerFullScreen(false));
          }
          break;
        case "f":
          // prevent overriding Ctrl+F shortcut
          if (!e.ctrlKey && !e.metaKey) handleFullScreen();
          break;
      }
    },
    [fullScreen]
  );

  useEventListener("keydown", handleKeyPress);

  const onMouseEnter = useCallback(() => {
    setHover(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    setHover(false);
  }, []);

  return (
    <Tooltip
      open={hover}
      slotProps={{
        popper: {
          container: containerRef.current,
        },
      }}
      followCursor={!fullScreen}
      placement="top"
      title={<KeyboardKey>f</KeyboardKey>}
    >
      <IconButton
        color="ghost"
        onClick={handleFullScreen}
        ref={containerRef}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {fullScreen ? <FullscreenExit /> : <Fullscreen />}
      </IconButton>
    </Tooltip>
  );
};

export const PipButton = () => {
  const dispatch = useAppDispatch();
  const [canPip, setCanPip] = useState<boolean>(false);

  useEffect(() => {
    setCanPip("pictureInPictureEnabled" in document && document.pictureInPictureEnabled);
  }, []);

  const onClickPip = useCallback(() => {
    dispatch(SetPlayerPip(true));
  }, []);

  if (!canPip) {
    return <></>;
  }
  return (
    <IconButton color="ghost" onClick={onClickPip}>
      <PictureInPictureAltOutlined />
    </IconButton>
  );
};

export const SettingsButton = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isFileTCDisplayed = useAppSelector(GetIsFileTCDisplayed);
  const playbackSpeed = useAppSelector(GetPlaybackSpeed);
  const timecodeIn = useAppSelector(GetTimecodeIn);
  const containerRef = useRef<HTMLButtonElement>(null);

  const [playerBackground, setPlayerBackground] = useState<"contrasted" | "default">(
    (localStorage.getItem("reactPlayer.playerBackground") as "contrasted" | "default") ?? "default"
  );

  const playbackRates = [0.25, 0.5, 1, 1.25, 1.5, 2, 3, 4];

  const [anchorMoreEl, setAnchorMoreEl] = useState<null | HTMLElement>(null);
  const moreOpen = Boolean(anchorMoreEl);
  const handleMoreClick = useCallback((event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    event.stopPropagation();
    setAnchorMoreEl(event.currentTarget);
  }, []);
  const handleMoreClose = useCallback(() => {
    setAnchorMoreEl(null);
  }, []);

  const handlePlaybackSpeedChange = useCallback((e: SelectChangeEvent<number>) => {
    dispatch(SetPlaybackSpeed(e.target.value as number));
  }, []);

  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName.toLowerCase() === "input" || activeElement.tagName.toLowerCase() === "textarea")
      ) {
        return;
      }

      switch (e.key) {
        case "j":
          if (playbackSpeed > playbackRates[0])
            dispatch(SetPlaybackSpeed(playbackRates[playbackRates.findIndex((x) => x === playbackSpeed) - 1]));
          break;
        case "l":
          if (playbackSpeed < playbackRates[playbackRates.length - 1])
            dispatch(SetPlaybackSpeed(playbackRates[playbackRates.findIndex((x) => x === playbackSpeed) + 1]));
          break;
        case "k":
          if (playbackSpeed !== 1) {
            dispatch(SetPlaybackSpeed(1));
          }
          dispatch(PlayPause());
          break;
      }
    },
    [playbackSpeed]
  );

  useEventListener("keydown", handleKeyPress);

  const handleTCDChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(SetFileTCDisplayed());
  }, []);

  const handleBackgroundChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPlayerBackground(e.target.checked ? "contrasted" : "default");
    localStorage.setItem("reactPlayer.playerBackground", e.target.checked ? "contrasted" : "default");
  };

  // dispatch player background changes to player
  useEffect(() => {
    dispatch(SetPlayerBackground(playerBackground));
  }, [playerBackground]);

  return (
    <>
      <IconButton color="ghost" onClick={handleMoreClick} ref={containerRef}>
        <SettingsOutlined />
      </IconButton>
      <Menu
        container={containerRef.current}
        open={moreOpen}
        anchorEl={anchorMoreEl}
        onClose={handleMoreClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={(theme) => ({ zIndex: theme.zIndex.snackbar + 1 })}
      >
        <ListItem
          dense
          sx={(theme) => ({
            gap: 2,
            paddingLeft: 2,
            paddingRight: 0.5,
            borderBottom: `.0625rem solid ${theme.palette.backgroundAlpha.border}`,
          })}
        >
          <Typography variant="body1" sx={{ flexGrow: 1 }}>
            {t("menu:item.contrastedBackground")}
          </Typography>
          <Switch checked={playerBackground === "contrasted"} onChange={handleBackgroundChange} />
        </ListItem>
        {timecodeIn !== "" && (
          <ListItem
            dense
            sx={(theme) => ({
              gap: 2,
              paddingLeft: 2,
              paddingRight: 0.5,
              borderBottom: `.0625rem solid ${theme.palette.backgroundAlpha.border}`,
            })}
          >
            <Typography variant="body1" sx={{ flexGrow: 1 }}>
              {t("component:body.realTimecodes")}
            </Typography>
            <Switch checked={isFileTCDisplayed} onChange={handleTCDChange} />
          </ListItem>
        )}
        <ListItem dense sx={{ gap: 2, paddingLeft: 2, paddingRight: 1, paddingTop: 1, paddingBottom: 1 }}>
          <Typography variant="body1" sx={{ flexGrow: 1 }}>
            {t("component:body.playbackSpeed")} (JKL)
          </Typography>
          <Select
            value={playbackSpeed}
            onChange={handlePlaybackSpeedChange}
            MenuProps={{
              sx: { zIndex: 1401 },
              container: containerRef.current,
            }}
          >
            {playbackRates.map((rate) => (
              <MenuItem key={`rate-${rate}`} value={rate}>
                {rate}x
              </MenuItem>
            ))}
          </Select>
        </ListItem>
      </Menu>
    </>
  );
};

export const TimecodeControls: React.FC<{ hidden?: boolean }> = ({ hidden = false }) => {
  const { t } = useTranslation();
  const [hover, setHover] = useState<boolean>(false);
  const frameRate = useAppSelector(GetFrameRate);
  const timecodeIn = useAppSelector(GetTimecodeIn);
  const isPlaying = useAppSelector(GetPlayingState);
  const dispatch = useAppDispatch();
  const isAdam = cacheService.GetPlatformMode() === PlatformMode.Adam;
  const minus5Ref = useRef<HTMLButtonElement>(null);
  const minus1Ref = useRef<HTMLButtonElement>(null);
  const plus1Ref = useRef<HTMLButtonElement>(null);
  const plus5Ref = useRef<HTMLButtonElement>(null);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const hideControls = useCallback(() => {
    clearTimeout(timerRef.current!);
    timerRef.current = setTimeout(() => setHover(false), 500);
  }, []);
  const showControls = useCallback(() => {
    clearTimeout(timerRef.current!);
    if (!hidden) setHover(true);
  }, [hidden]);

  const handleFastForward = useCallback(
    (frameCount: number, pause: boolean) => {
      if (isPlaying && pause) dispatch(PlayPause());
      dispatch(GoToFrameDelta(frameCount));
    },
    [isPlaying]
  );

  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName.toLowerCase() === "input" || activeElement.tagName.toLowerCase() === "textarea")
      ) {
        return;
      }

      switch (e.key) {
        case "ArrowLeft":
          if (e.shiftKey) {
            handleFastForward(-5 * frameRate, false);
          } else {
            handleFastForward(-1, true);
          }
          break;
        case "ArrowRight":
          if (e.shiftKey) {
            handleFastForward(5 * frameRate, false);
          } else {
            handleFastForward(1, true);
          }
          break;
        case "I":
          if (e.shiftKey) {
            dispatch(SetActiveTimeline(TimelineType.Clips));
            dispatch(SetCreatingOrEditingTimelineEvent({ creatingOrEditing: true }));
          }
          break;

        default:
          break;
      }
    },
    [handleFastForward]
  );

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      if (e.shiftKey) {
        e.preventDefault();
        handleFastForward(e.deltaY > 0 ? -1 : 1, true);
      }
    },
    [handleFastForward]
  );

  useEventListener("keydown", handleKeyPress);
  useEventListener("wheel", handleWheel);

  const onClickFirst = useCallback(() => {
    dispatch(GoToFrame(0));
  }, []);

  const onClickMinus5s = useCallback(() => {
    handleFastForward(-5 * frameRate, false);
  }, [handleFastForward, frameRate]);

  const onClickMinus1f = useCallback(() => {
    handleFastForward(-1, true);
  }, [handleFastForward]);

  const onClickPlus5s = useCallback(() => {
    handleFastForward(5 * frameRate, false);
  }, [handleFastForward, handleFastForward]);

  const onClickPlus1f = useCallback(() => {
    handleFastForward(1, true);
  }, [handleFastForward]);

  const onClickLast = useCallback(() => {
    dispatch(GoToLastFrame());
  }, []);

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      gap={0.5}
      onMouseEnter={showControls}
      onMouseLeave={hideControls}
    >
      <Collapse in={hover} orientation="horizontal">
        <Stack direction="row" gap={0.5}>
          {isAdam && (
            <Button size="small" variant="text" onClick={onClickFirst}>
              {t("common:first")}
            </Button>
          )}
          <Tooltip
            placement="top"
            slotProps={{
              popper: {
                container: minus5Ref.current,
              },
            }}
            followCursor
            title={
              <Stack direction="row" gap={0.5} alignItems={"center"}>
                <KeyboardKey>{t("tooltip:title.shift")}</KeyboardKey>
                <Typography variant="body2" color="text.tier">
                  +
                </Typography>
                <KeyboardKey>
                  <ArrowLeft />
                </KeyboardKey>
              </Stack>
            }
          >
            <span>
              <Button
                ref={minus5Ref}
                //disabled={currTime < 5}
                sx={{ width: 32 }}
                variant="text"
                onClick={onClickMinus5s}
              >
                -5s
              </Button>
            </span>
          </Tooltip>
          {timecodeIn && (
            <Tooltip
              slotProps={{
                popper: {
                  container: minus1Ref.current,
                },
              }}
              placement="top"
              followCursor
              title={
                <KeyboardKey>
                  <ArrowLeft />
                </KeyboardKey>
              }
            >
              <Button ref={minus1Ref} sx={{ width: 32 }} variant="text" onClick={onClickMinus1f}>
                -1f
              </Button>
            </Tooltip>
          )}
        </Stack>
      </Collapse>
      {!hidden && <TimecodeInput />}
      <Collapse in={hover} orientation="horizontal">
        <Stack direction="row" gap={0.5}>
          {timecodeIn && (
            <Tooltip
              slotProps={{
                popper: {
                  container: plus1Ref.current,
                },
              }}
              followCursor
              placement="top"
              title={
                <KeyboardKey>
                  <ArrowRight />
                </KeyboardKey>
              }
            >
              <Button ref={plus1Ref} sx={{ width: 32 }} variant="text" onClick={onClickPlus1f}>
                +1f
              </Button>
            </Tooltip>
          )}
          <Tooltip
            placement="top"
            slotProps={{
              popper: {
                container: plus5Ref.current,
              },
            }}
            followCursor
            title={
              <Stack direction="row" gap={0.5} alignItems={"center"}>
                <KeyboardKey>{t("tooltip:title.shift")}</KeyboardKey>
                <Typography variant="body2" color="text.tier">
                  +
                </Typography>
                <KeyboardKey>
                  <ArrowRight />
                </KeyboardKey>
              </Stack>
            }
          >
            <span>
              <Button
                ref={plus5Ref}
                //disabled={currTime > duration - 5}
                sx={{ width: 32 }}
                variant="text"
                onClick={onClickPlus5s}
              >
                +5s
              </Button>
            </span>
          </Tooltip>
          {isAdam && (
            <Button size="small" variant="text" onClick={onClickLast}>
              {t("button:last")}
            </Button>
          )}
        </Stack>
      </Collapse>
    </Stack>
  );
};

export const TimecodeInput = () => {
  const [inputFocused, setInputFocused] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const isFileTCDisplayed = useAppSelector(GetIsFileTCDisplayed);
  const timecodeIn = useAppSelector(GetTimecodeIn);
  const frameRate = useAppSelector(GetFrameRate);
  const timecode = useAppSelector(GetTimecode);
  const fileTimecode = useAppSelector(GetFileTimecode);
  const timecodeFinal = isFileTCDisplayed ? fileTimecode : timecode;

  const [timecodeEdit, setTimecodeEdit] = useState<string>(isFileTCDisplayed ? fileTimecode : timecode);

  useEffect(() => {
    setTimecodeEdit(timecodeFinal);
  }, [inputFocused]);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter") {
        setInputFocused(false);
        dispatch(
          SetSeek(
            TimecodeToFrame(
              isFileTCDisplayed ? FileTimecodeToTimecode(timecodeIn, e.target.value, frameRate) : e.target.value,
              frameRate
            )
          )
        );
      }
    },
    [isFileTCDisplayed, timecodeIn, frameRate]
  );

  const onClickStack = useCallback(() => {
    setInputFocused(true);
  }, []);
  const onClickTimecode = useCallback(() => {
    setInputFocused(false);
  }, []);

  const t = useTheme();

  const sxContainer = useMemo(
    () => ({
      transition: t.transitions.create(["width"], {
        duration: t.transitions.duration.short,
        easing: t.transitions.easing.easeInOut,
      }),
    }),
    [t]
  );
  const sxStack = useMemo(
    () => ({
      height: 32,
      borderRadius: 1,
      boxSizing: "border-box",
      border: `.0625rem solid transparent`,
      transition: t.transitions.create(["all"], {
        duration: t.transitions.duration.short,
        easing: t.transitions.easing.easeInOut,
      }),
      "&:hover": {
        border: `.0625rem solid ${t.palette.backgroundAlpha.border}`,
        background: t.palette.backgroundAlpha.hover,
      },
    }),
    [t]
  );

  const sxTypo = useMemo(() => ({ width: 24, textAlign: "center" }), []);

  const sxInput = useMemo(() => ({ ...t.typography.subtitle2, width: 140, height: 32 }), [t]);

  return (
    <ClickAwayListener onClickAway={onClickTimecode}>
      <Stack sx={sxContainer}>
        {inputFocused ? (
          <InputTimecode
            frameRate={frameRate}
            handleKeyDown={handleKeyPress}
            value={timecodeEdit}
            setValue={setTimecodeEdit}
            // onMouseLeave={onClickTimecode}
            size="small"
            endAdornment={<KeyboardReturn />}
            sx={sxInput}
          />
        ) : (
          <Stack direction="row" alignItems={"center"} gap={0} onClick={onClickStack} px={1} py={0.5} sx={sxStack}>
            <Typography variant="subtitle2" component="span" color="text.primary" sx={sxTypo}>
              {timecodeFinal.slice(0, 2)}
            </Typography>
            <Typography variant="subtitle2" component="span" color="text.tier">
              :
            </Typography>
            <Typography variant="subtitle2" component="span" color="text.primary" sx={sxTypo}>
              {timecodeFinal.slice(3, 5)}
            </Typography>
            <Typography variant="subtitle2" component="span" color="text.tier">
              :
            </Typography>
            <Typography variant="subtitle2" component="span" color="text.primary" sx={sxTypo}>
              {timecodeFinal.slice(6, 8)}
            </Typography>
            <Typography variant="subtitle2" component="span" color="text.tier">
              :
            </Typography>
            <Typography variant="subtitle2" component="span" color="text.tier" sx={sxTypo}>
              {timecodeIn
                ? timecode.slice(9, timecodeFinal.length)
                : FrameToMs(parseInt(timecodeFinal.slice(9, timecodeFinal.length)), frameRate)}
            </Typography>
          </Stack>
        )}
      </Stack>
    </ClickAwayListener>
  );
};

export const KeyboardKey = styled("div")(({ theme }) => ({
  paddingTop: theme.spacing(0.25),
  paddingBottom: theme.spacing(0.25),
  paddingLeft: theme.spacing(0.25),
  paddingRight: theme.spacing(0.25),
  border: ".0625rem solid white",
  borderRadius: ".25rem",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: 24,
  minWidth: 24,
  ...theme.typography.body1,
  fontWeight: 500,
}));

export const ScreenshotButton = () => {
  const { t } = useTranslation();

  //const seconds = useAppSelector(GetTimeInSec);
  const { objectId, objectLayer, objectType } = useAppSelector(GetPlayerSetupData);
  const enqueueCustomSnackbar = useCustomSnackbar();
  const containerRef = useRef<HTMLButtonElement>(null);
  const currentFrame = useAppSelector(GetRealCurrentFrame);

  const [hover, setHover] = useState<boolean>(false);
  const [defaultRes, setDefaultRes] = useState<"low" | "high">(
    objectType === ObjectType.Video ? (localStorage.getItem("screenshotRes") as "low" | "high") ?? "low" : "low"
  );

  const screenshot = (res: "low" | "high") => {
    if (objectId !== undefined) {
      if (
        res === "high" &&
        UserHasAcl(AclTypes.aclScreenshotFrameHires) &&
        UserHasLicence(Licences.PlayerScreenshotFromHires)
      ) {
        mediaService.GetScreenshotFromAssetVideoHires(objectId, Math.round(currentFrame), cacheService.GetAuthKey()!);
      } else {
        mediaService.GetScreenshotFromAssetVideoProxy(objectId, Math.round(currentFrame), cacheService.GetAuthKey()!);
      }
    }
  };

  const [anchorMoreEl, setAnchorMoreEl] = useState<null | HTMLElement>(null);
  const moreOpen = Boolean(anchorMoreEl);
  const handleMoreClick = (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    event.stopPropagation();
    setAnchorMoreEl(event.currentTarget);
  };
  const handleMoreClose = useCallback(() => {
    setAnchorMoreEl(null);
    setHover(false);
  }, []);

  const handleSelectDefaultRes = (res: "low" | "high") => {
    setDefaultRes(res);
    localStorage.setItem("screenshotRes", res);
    screenshot(res);
    handleMoreClose();
  };

  if (
    !UserHasAcl(AclTypes.aclScreenshotFrame) ||
    (objectType !== ObjectType.Video && objectType !== ObjectType.DcpPackage) ||
    objectLayer !== ObjectLayer.Asset
  ) {
    return <></>;
  }

  return (
    <>
      <IconButton
        color="ghost"
        ref={containerRef}
        onClick={(e) => screenshot(defaultRes)}
        sx={{ width: "unset" }}
        onMouseEnter={(e) => setHover(true)}
        onMouseLeave={(e) => !moreOpen && setHover(false)}
      >
        <Collapse in={hover} orientation="horizontal">
          <Stack
            onClick={handleMoreClick}
            sx={(theme) => ({
              borderRadius: ".125rem",
              boxSizing: "border-box",
              border: `.0625rem solid transparent`,
              "&:hover": {
                background: theme.palette.backgroundAlpha.hover,
                border: `.0625rem solid ${theme.palette.backgroundAlpha.border}`,
              },
              mr: 1,
              height: "100%",
              fill: "text.tier",
            })}
          >
            <ExpandMoreOutlined />
          </Stack>
        </Collapse>
        <Stack position="relative" sx={{ justifyContent: "center", alignItems: "center" }}>
          <CameraAltOutlined />
          {defaultRes === "high" && objectType === ObjectType.Video && (
            <Typography
              sx={(theme) => ({
                background: theme.palette.info.main,
                color: theme.palette.text.primary,
                padding: ".0625rem .125rem",
                position: "absolute",
                top: -4,
                right: -8,
                fontWeight: 500,
                borderRadius: ".125rem",
              })}
              variant="body2"
            >
              HR
            </Typography>
          )}
        </Stack>
      </IconButton>

      <Menu
        open={moreOpen}
        anchorEl={anchorMoreEl}
        container={containerRef.current}
        sx={(theme) => ({ zIndex: theme.zIndex.snackbar + 1 })}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        onClose={handleMoreClose}
      >
        {objectType === ObjectType.Video && (
          <MenuItem
            key={"highres"}
            onClick={(e) => handleSelectDefaultRes("high")}
            disabled={
              !(
                UserHasAcl(AclTypes.aclScreenshotFrame) &&
                UserHasAcl(AclTypes.aclScreenshotFrameHires) &&
                UserHasLicence(Licences.PlayerScreenshotFromHires)
              )
            }
          >
            {t("menu:item.screenshotInHighRes")}
          </MenuItem>
        )}
        <MenuItem
          key={"lowres"}
          onClick={(e) => handleSelectDefaultRes("low")}
          disabled={!UserHasAcl(AclTypes.aclScreenshotFrame)}
        >
          {t("menu:item.screenshotInLowRes")}
        </MenuItem>
      </Menu>
    </>
  );
};

export const ThumbnailButton = () => {
  const { t } = useTranslation();
  const store: Store = useStore();
  const frameRate = useAppSelector(GetFrameRate);
  const { objectId, objectLayer, objectType } = useAppSelector(GetPlayerSetupData);
  const enqueueCustomSnackbar = useCustomSnackbar();
  const thumbnailButton = useRef(null);

  const handleSetFrameAsThumbnail = () => {
    const seconds = store.getState().reactPlayer.interface.timeInSec;
    if (objectId === undefined) return;

    thumbnailService.CreateThumbReference(objectId, SecToFrame(seconds, frameRate!, true)).then((res) => {
      if (res.success) {
        enqueueCustomSnackbar(t("alert:success.thumbnailReferenceChanged"), "success");
      } else {
        enqueueCustomSnackbar(res.message || t("alert:error.changingThumbnail"), "error");
      }
    });
  };

  return (
    <Tooltip
      slotProps={{
        popper: {
          container: thumbnailButton.current,
        },
      }}
      title={t("tooltip:title.setCurrentFrameAsThumbnail")}
    >
      <IconButton color="ghost" ref={thumbnailButton} onClick={handleSetFrameAsThumbnail}>
        <ImageOutlined />
      </IconButton>
    </Tooltip>
  );
};

export const LanguagesButton = () => {
  const { t } = useTranslation();
  const { activeCaptionIndex, activeAudioTrackIndex, captions, audioTracks } = useAppSelector(GetLanguages);
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLButtonElement>(null);

  // Menu
  const [anchorMoreEl, setAnchorMoreEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    setTimeout(() => setAnchorMoreEl(null), 50);
  }, [activeCaptionIndex, activeAudioTrackIndex, captions, audioTracks]);
  const moreOpen = Boolean(anchorMoreEl);
  const handleMoreClick = useCallback((event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    event.stopPropagation();
    setAnchorMoreEl(event.currentTarget);
  }, []);
  const handleMoreClose = useCallback(() => {
    setAnchorMoreEl(null);
  }, []);

  const handleSetCaption: (index: number) => void = useCallback((index) => {
    dispatch(SetActiveLanguages({ activeCaptionIndex: index, activeAudioTrackIndex: null }));
    handleMoreClose();
  }, []);

  const handleSetAudioTrack: (index: number) => void = useCallback((index) => {
    dispatch(SetActiveLanguages({ activeCaptionIndex: null, activeAudioTrackIndex: index }));
    handleMoreClose();
  }, []);

  if (
    (captions === null || captions.filter((x) => x.label).length === 0) &&
    (audioTracks === null || audioTracks!.length <= 1)
  ) {
    return <></>;
  }

  return (
    <>
      <IconButton color="ghost" onClick={handleMoreClick} ref={containerRef}>
        <SubtitlesOutlined color={activeCaptionIndex && activeCaptionIndex > 0 ? "info" : "inherit"} />
      </IconButton>
      <Menu
        container={containerRef.current}
        open={moreOpen}
        anchorEl={anchorMoreEl}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        keepMounted
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        onClose={handleMoreClose}
        sx={(theme) => ({ zIndex: theme.zIndex.snackbar + 1 })}
      >
        <Stack direction="row">
          {captions !== null && captions !== undefined && captions.length > 0 && (
            <Stack
              gap={0}
              sx={(theme) => ({
                maxHeight: 300,
                overflowY: "scroll",
                borderRight: `.0625rem solid ${theme.palette.backgroundAlpha.border}`,
                ...theme.helpers(theme).scrollableContainer,
              })}
            >
              <Box
                px={2}
                py={1}
                sx={(theme) => ({
                  position: "sticky",
                  zIndex: 2,
                  top: 0,
                  background: theme.palette.background.paper,
                })}
              >
                <Typography variant="subtitle2" fontWeight={500}>
                  {t("component:body.captions")}
                </Typography>
              </Box>
              {captions.map((menuCaption, index) => (
                <MenuItem
                  onClick={(e) => handleSetCaption(index)}
                  key={`caption-${menuCaption.url}`}
                  sx={(theme) => ({
                    ...(index === activeCaptionIndex && { backgroundColor: theme.palette.primary.alpha }),
                  })}
                >
                  <ListItemText>{menuCaption.label}</ListItemText>
                  {index === activeCaptionIndex && <CheckOutlined fontSize="small" color="primary" />}
                </MenuItem>
              ))}
            </Stack>
          )}
          {audioTracks !== null && audioTracks !== undefined && audioTracks.length > 1 && (
            <Stack gap={0} sx={(theme) => ({ maxHeight: 300, overflowY: "scroll", ...theme.helpers(theme).scrollBar })}>
              <Box
                px={2}
                py={1}
                sx={(theme) => ({
                  position: "sticky",
                  zIndex: 2,
                  top: 0,
                  background: theme.palette.background.paper,
                })}
              >
                <Typography variant="subtitle2" fontWeight={500}>
                  {t("common:audio")}
                </Typography>
              </Box>
              {audioTracks.map((menuAudioTrack, index) => (
                <MenuItem
                  onClick={(e) => handleSetAudioTrack(index)}
                  key={`audioTrack-${index}`}
                  sx={(theme) => ({
                    ...(index === activeAudioTrackIndex && { backgroundColor: theme.palette.primary.alpha }),
                  })}
                >
                  <ListItemText>{menuAudioTrack.name}</ListItemText>
                  {index === activeAudioTrackIndex && <CheckOutlined fontSize="small" color="primary" />}
                </MenuItem>
              ))}
            </Stack>
          )}
        </Stack>
      </Menu>
    </>
  );
};
